/**
 * Helper: Shopify
 * -----------------------------------------------------------------------------
 * Shopify helper functions.
 *
 */
import productByIdQuery from '~graphql/product-by-id.gql'
import productByIdEnQuery from '~graphql/product-by-id-en.gql'
import productsByIdQuery from '~graphql/products-by-id.gql'
import productsByIdEnQuery from '~graphql/products-by-id-en.gql'
import productQuery from '~graphql/product.gql'
import productEnQuery from '~graphql/product-en.gql'

import { formatProduct } from '~/format/format-product'

/**
 * Performs a Shopify API call for localized product data.
 * @param {Array} ids - Shopify product ids.
 * @param {Boolean} [useCache] - Use cache.
 * @returns {Object}
 */
export async function shopifyProductsByIdQuery(ids, useCache = false) {
  let response = null

  try {
    const queries = [
      cnvs.Query({
        query: productsByIdQuery,
        variables: {
          country: cnvs.store.country,
          ids,
          language: cnvs.store.language,
        },
      }, useCache),
    ]

    if (cnvs.store.language !== 'en') {
      queries.push(cnvs.Query({
        query: productsByIdEnQuery,
        variables: { ids },
      }, useCache))
    }

    response = await Promise.all(queries)
  } catch (event) {
    window.console.error('[Shopify] Error querying the Storefront API.', event)
  }

  if (!response?.[0]?.nodes) {
    return false
  }

  return response[0].nodes?.filter((prod) => prod !== null).map((prod) => {
    const prodEn = response[1]?.nodes?.find((pr) => pr && pr.id === prod.id)
    return formatProduct([
      {
        product: prod,
      },
      {
        product: prodEn,
      },
    ])
  }) || []
}

/**
 * Performs a Shopify API call for localized product data.
 * @param {String} id - Shopify product id.
 * @param {Boolean} [useCache] - Use cache.
 * @returns {Object}
 */
export async function shopifyProductByIdQuery(id, useCache = false) {
  let response = null

  try {
    const queries = [
      cnvs.Query({
        query: productByIdQuery,
        variables: {
          country: cnvs.store.country,
          id,
          language: cnvs.store.language,
        },
      }, useCache),
    ]

    if (cnvs.store.language !== 'en') {
      queries.push(cnvs.Query({
        query: productByIdEnQuery,
        variables: { id },
      }, useCache))
    }

    response = await Promise.all(queries)
  } catch (event) {
    window.console.error('[Shopify] Error querying the Storefront API.', event)
  }

  if (!response?.[0]?.product) {
    return false
  }

  return formatProduct(response)
}

/**
 * Query and format product.
 * - Queries both storefront API and product merge template.
 * @param {String} handle - Product handle to query.
 * @returns {Promise<Object>}
 */
export function queryFormatProduct(handle) {
  return new Promise(async(resolve, reject) => {
    try {
      const storefrontQuery = cnvs.Query({
        query: productQuery,
        variables: {
          country: cnvs.store.country,
          handle,
          language: cnvs.store.language,
        },
      })

      const mergeQuery = fetch(`${cnvs.routes.normalisedRoot}products/${handle}?view=merge`)
      const queue = [storefrontQuery, mergeQuery]

      if (cnvs.store.language !== 'en') {
        queue.push(cnvs.Query({
          query: productEnQuery,
          variables: { handle },
        }))
      }

      const [response, mergeResponse, enResponse] = await Promise.all(queue)
      const merge = await mergeResponse.json()

      if (cnvs.settings.disableComponentReadyState) {
        return
      }

      const formattedProduct = formatProduct([response, enResponse], { merge })
      resolve(formattedProduct)

    } catch (error) {
      reject(error)
    }
  })
}
